import React, { useRef, useState } from "react";

// 쪽 위에 펜으로 그리기(2026-10-19 대표). 'Draw or stick a picture' 같은 칸에 아이가 직접 그린다.
// 지인에듀는 그리는 칸 자리를 주지 않아서 쪽 어디에나 그릴 수 있게 둔다.
//
// - 획은 쪽 비율 좌표(0~1)로 저장한다. 화면 크기 · 확대와 상관없이 같은 자리에 그려지고 해설지(readers_pdf)도 같다.
// - 그리기를 켠 동안만 손가락 · 마우스를 받는다. 끄면 그림은 보이기만 하고 아래 답 칸을 누를 수 있다.
// - 지우개는 손이 지나간 획을 통째로 지운다.

// 쪽 비율(A4, EbookPage 의 aspect-ratio 와 같다). viewBox 를 이 비율로 두어 획이 찌그러지지 않는다.
export const DRAWING_VIEW_WIDTH = 1000;
export const DRAWING_VIEW_HEIGHT = Math.round((1000 * 1170) / 827);
export const PEN_COLOR_LIST = ["#1F2328", "#E0312B", "#F08A24", "#F2C200", "#1F9D6B", "#2F6FDB", "#8B4FD6"];
export const PEN_WIDTH = 5;
// 손이 조금 움직일 때마다 점을 다 찍으면 획이 무거워진다. 이만큼(쪽 폭 비율) 움직여야 점을 더한다.
const MINIMUM_POINT_DISTANCE = 0.003;
const MAXIMUM_POINT_COUNT = 600;
export const MAXIMUM_STROKE_COUNT = 300;
// 지우개가 닿았다고 보는 거리(viewBox 단위)
const ERASER_RADIUS = 22;

const roundRatio = (value) => Math.round(Math.min(Math.max(value, 0), 1) * 10000) / 10000;

export const strokePointsAttribute = (stroke) =>
  (stroke.points || [])
    .map(([x, y]) => `${(x * DRAWING_VIEW_WIDTH).toFixed(1)},${(y * DRAWING_VIEW_HEIGHT).toFixed(1)}`)
    .join(" ");

const DrawingLayer = ({ strokeList, isDrawing, color, isEraser, onChange }) => {
  const svgRef = useRef(null);
  const drawingRef = useRef(null);
  const [liveStroke, setLiveStroke] = useState(null);

  const ratioPointOf = (event) => {
    const rect = svgRef.current.getBoundingClientRect();
    return [roundRatio((event.clientX - rect.left) / rect.width), roundRatio((event.clientY - rect.top) / rect.height)];
  };

  const eraseAt = ([x, y]) => {
    const remaining = strokeList.filter(
      (stroke) =>
        !(stroke.points || []).some(
          ([pointX, pointY]) =>
            Math.hypot((pointX - x) * DRAWING_VIEW_WIDTH, (pointY - y) * DRAWING_VIEW_HEIGHT) < ERASER_RADIUS
        )
    );
    if (remaining.length !== strokeList.length) onChange(remaining);
  };

  const handlePointerDown = (event) => {
    if (!isDrawing) return;
    event.preventDefault();
    svgRef.current.setPointerCapture(event.pointerId);
    const point = ratioPointOf(event);
    if (isEraser) {
      drawingRef.current = { isErasing: true };
      eraseAt(point);
      return;
    }
    if (strokeList.length >= MAXIMUM_STROKE_COUNT) return;
    drawingRef.current = { color, points: [point] };
    setLiveStroke(drawingRef.current);
  };

  const handlePointerMove = (event) => {
    const current = drawingRef.current;
    if (!isDrawing || !current) return;
    const point = ratioPointOf(event);
    if (current.isErasing) {
      eraseAt(point);
      return;
    }
    const last = current.points[current.points.length - 1];
    if (Math.hypot(point[0] - last[0], point[1] - last[1]) < MINIMUM_POINT_DISTANCE) return;
    if (current.points.length >= MAXIMUM_POINT_COUNT) return;
    drawingRef.current = { ...current, points: [...current.points, point] };
    setLiveStroke(drawingRef.current);
  };

  const handlePointerUp = () => {
    const current = drawingRef.current;
    drawingRef.current = null;
    setLiveStroke(null);
    if (!current || current.isErasing) return;
    // 한 번 톡 찍은 것도 점으로 남긴다
    const points = current.points.length === 1 ? [current.points[0], current.points[0]] : current.points;
    onChange([...strokeList, { color: current.color, points }]);
  };

  return (
    <svg
      ref={svgRef}
      className={`page-drawing${isDrawing ? " page-drawing--active" : ""}${isEraser ? " page-drawing--eraser" : ""}`}
      viewBox={`0 0 ${DRAWING_VIEW_WIDTH} ${DRAWING_VIEW_HEIGHT}`}
      preserveAspectRatio="none"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {[...strokeList, ...(liveStroke ? [liveStroke] : [])].map((stroke, index) => (
        <polyline
          key={index}
          points={strokePointsAttribute(stroke)}
          fill="none"
          stroke={stroke.color}
          strokeWidth={PEN_WIDTH}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
};

export default DrawingLayer;
